import { useEffect, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Shield, Plus, Trash2, AlertTriangle, Clock, Zap } from 'lucide-react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { Badge } from '@/components/ui/Badge'
import { godModeIpBlocks } from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'

interface IpBlock {
  id: string
  cidr: string
  reason: string | null
  enabled: boolean
  blocked_count: number
  last_blocked_at: string | null
  recent_matches: number
  recent_burst: boolean
  created_at: string
}

interface IpBlocksProps {
  current_user: CurrentUser
  god_mode: boolean
  ip_blocks: IpBlock[]
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never'
  const diffMs = Date.now() - new Date(dateString).getTime()
  const diffHours = Math.floor(diffMs / 3600000)
  if (diffHours < 1) return `${Math.floor(diffMs / 60000)}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return new Date(dateString).toLocaleDateString()
}

export default function IpBlocks({ current_user, god_mode, ip_blocks }: IpBlocksProps) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [newCidr, setNewCidr] = useState('')
  const [newReason, setNewReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
  }, [flash?.notice])

  const handleCreate = () => {
    if (!newCidr.trim()) return
    setSubmitting(true)
    router.post(
      godModeIpBlocks.create.path(),
      { ip_block: { cidr: newCidr.trim(), reason: newReason.trim() || null } },
      { onFinish: () => { setSubmitting(false); setNewCidr(''); setNewReason('') } }
    )
  }

  const handleToggle = (block: IpBlock) => {
    router.patch(godModeIpBlocks.update.path({ id: block.id }), { ip_block: { enabled: !block.enabled } })
  }

  const handleDelete = (block: IpBlock) => {
    router.delete(godModeIpBlocks.destroy.path({ id: block.id }))
  }

  return (
    <>
      <Head title="IP Blocks - God Mode" />
      <MainLayout
        user={current_user}
        breadcrumbs={[{ label: 'God Mode' }, { label: 'IP Blocks' }]}
        godMode={god_mode}
      >
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="size-6 text-amber-500" />
                <h1 className="text-2xl font-semibold">IP Blocks</h1>
              </div>
              <p className="text-muted-foreground">
                Block IP ranges (CIDR) from creating visitors. Blocks are checked during widget
                /init.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="size-5" />
                  Add New Block
                </CardTitle>
                <CardDescription>
                  Enter a CIDR range (e.g., 43.163.0.0/16) to block
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    placeholder="CIDR (e.g., 43.163.0.0/16)"
                    value={newCidr}
                    onChange={(e) => setNewCidr(e.target.value)}
                    className="w-52"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                  <Input
                    placeholder="Reason (optional)"
                    value={newReason}
                    onChange={(e) => setNewReason(e.target.value)}
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                  <Button onClick={handleCreate} disabled={submitting || !newCidr.trim()}>
                    <Plus className="size-4 mr-1" />
                    Add Block
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Active Blocks</CardTitle>
                <CardDescription>
                  {ip_blocks.length} block{ip_blocks.length !== 1 ? 's' : ''} configured
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ip_blocks.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No IP blocks configured yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-[1fr_1fr_100px_120px_120px_80px_60px] gap-4 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                      <div>CIDR Range</div>
                      <div>Reason</div>
                      <div>Blocked</div>
                      <div>Last Blocked</div>
                      <div>Recent (6h)</div>
                      <div>Enabled</div>
                      <div></div>
                    </div>
                    {ip_blocks.map((block) => (
                      <div
                        key={block.id}
                        className={`grid grid-cols-[1fr_1fr_100px_120px_120px_80px_60px] gap-4 px-3 py-3 rounded-lg border items-center ${
                          !block.enabled ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="font-mono text-sm">{block.cidr}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {block.reason || '-'}
                        </div>
                        <div className="text-sm font-medium">{block.blocked_count}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="size-3" />
                          {formatRelativeTime(block.last_blocked_at)}
                        </div>
                        <div className="flex items-center gap-2">
                          {block.recent_matches > 0 ? (
                            <>
                              <Badge
                                variant={block.recent_burst ? 'destructive' : 'secondary'}
                                className="text-xs"
                              >
                                {block.recent_matches}
                              </Badge>
                              {block.recent_burst && (
                                <span title="Burst pattern detected (3+ hits within 5s)">
                                  <Zap className="size-4 text-amber-500" />
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground text-sm">0</span>
                          )}
                        </div>
                        <div>
                          <Switch
                            checked={block.enabled}
                            onCheckedChange={() => handleToggle(block)}
                          />
                        </div>
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(block)}
                            className="text-destructive hover:text-destructive cursor-pointer"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-amber-500/30">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Detection Tips</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        <strong>Recent (6h)</strong>: Visitors from this CIDR in last 6 hours with
                        zero heartbeats
                      </li>
                      <li>
                        <strong>Burst</strong> <Zap className="size-3 inline text-amber-500" />:
                        3+ visitors created within 5 seconds (bot farm indicator)
                      </li>
                      <li>
                        Bot farms typically show 100+ recent matches with burst patterns
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayout>
    </>
  )
}
